import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';

import { apiService } from '../../services/apiService';
import type { Bin } from '../../services/types';

interface BinSearchProps {
  value: string;
  onChange: (description: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
}

export const BinSearch: React.FC<BinSearchProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = 'e.g., E2-03-07',
  required = false,
}) => {
  const [bins, setBins] = useState<Bin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [filteredBins, setFilteredBins] = useState<Bin[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [pendingBlur, setPendingBlur] = useState(false);
  const prevValueRef = useRef(value);

  useEffect(() => {
    const fetchBins = async () => {
      try {
        setLoading(true);
        const fetchedBins = await apiService.getBins();
        setBins(fetchedBins);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load bins');
      } finally {
        setLoading(false);
      }
    };

    fetchBins();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (value.trim() === '') {
      setFilteredBins([]);
      return;
    }

    const searchTerm = value.toLowerCase();
    const filtered = bins
      .filter(
        (bin) =>
          bin.description.toLowerCase().includes(searchTerm) ||
          bin.binNum.toLowerCase().includes(searchTerm)
      )
      .slice(0, 10);

    setFilteredBins(filtered);
  }, [value, bins]);

  // Trigger onBlur after parent state update (when value prop changes)
  useEffect(() => {
    if (pendingBlur && prevValueRef.current !== value) {
      setPendingBlur(false);
      if (onBlur) {
        onBlur();
      }
    }
    prevValueRef.current = value;
  }, [value, pendingBlur, onBlur]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setIsOpen(true);
  };

  const handleSelectBin = (bin: Bin) => {
    onChange(bin.description);
    setIsOpen(false);
    // Set flag to trigger onBlur after parent state updates
    setPendingBlur(true);
  };

  const handleInputFocus = () => {
    if (value.trim() !== '' && filteredBins.length > 0) {
      setIsOpen(true);
    }
  };

  if (loading) {
    return (
      <div className="relative">
        <div className="flex items-center gap-2 rounded-md border border-gray-300 bg-gray-50 px-3 py-2">
          <Search className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-500">Loading bins...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative">
        <div className="flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2">
          <span className="text-sm text-red-600">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          required={required}
          className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {isOpen && filteredBins.length > 0 && (
        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-300 bg-white shadow-lg">
          {filteredBins.map((bin) => (
            <button
              key={bin.binNum}
              type="button"
              onClick={() => handleSelectBin(bin)}
              className="flex w-full flex-col items-start px-4 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
            >
              <span className="font-medium text-gray-900">
                {bin.description}
              </span>
              <span className="text-sm text-gray-500">Bin: {bin.binNum}</span>
            </button>
          ))}
        </div>
      )}

      {isOpen && value.trim() !== '' && filteredBins.length === 0 && (
        <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-300 bg-white px-4 py-2 shadow-lg">
          <span className="text-sm text-gray-500">
            No bins found matching "{value}"
          </span>
        </div>
      )}
    </div>
  );
};
